import React, { Component } from 'react';
import { asyncConnect } from 'redux-connect';
import { translate } from 'react-i18next';
import Button from 'react-bootstrap/lib/Button';
import Checkbox from 'react-bootstrap/lib/Checkbox';
import Col from 'react-bootstrap/lib/Col';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import FormControl from 'react-bootstrap/lib/FormControl';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import HelpBlock from 'react-bootstrap/lib/HelpBlock';
import Alert from 'react-bootstrap/lib/Alert';
import Grid from 'react-bootstrap/lib/Grid';
import Row from 'react-bootstrap/lib/Row';

import { registerUser, login, restorePassword } from 'actions';
import I18nLink, { redirect } from 'widgets/I18nLink';
import { createValidator } from 'utils/validate';

import {
  defaultValidateHandler,
  defaultValidateStatus,
  defaultOnChange,
  defaultOnCheckboxChange,
  defaultOnBlur
} from 'utils/form';

import './auth-page.css';

class RegisterForm extends Component {
  constructor(...args) {
    super(...args);

    this.state = {
      name: '',
      email: '',
      password: '',
      passwordConfirm: '',
      termsOfAgreement: false,
      errors: {}
    };

    this.validateHandler = defaultValidateHandler.bind(this);
    this.validationState = defaultValidateStatus.bind(this);

    this.handleFieldChange = defaultOnChange.bind(this);
    this.handleCheckboxChange = defaultOnCheckboxChange.bind(this);
    this.handleFieldBlur = defaultOnBlur.bind(this);
  }

  componentWillMount() {
    const { t } = this.props;

    const rules = {
      name: {
        minlength: {
          data: 2,
          message: t('Must be at least 2 characters')
        }
      },
      email: {
        email: t('Enter valid email address')
      },
      password: {
        minlength: {
          data: 6,
          message: t('Must be at least 6 characters')
        }
      },
      passwordConfirm: {
        equals: t('Passwords must match')
      }
    };

    this.validate = createValidator(rules, { equals: this.validatePasswords })
  }

  validatePasswords = (value, rule) =>
    this.state.password === this.state.passwordConfirm;

  handleSubmit = (event) => {
    event.preventDefault();

    const passwordConfirm = this.validateHandler();
    if (!passwordConfirm) {
      return;
    }

    const { name, email, password } = this.state;

    this.props.registerUser({ name, email, password })
      .then(action => {
        if (action.error) {
          this.setState({ errors: action.error })
        } else if (action.statusCode === 201) {
          redirect('/');
        }
      });
  };

  getProperties = () => {
    const { name, email, password, passwordConfirm } = this.state;
    return { name, email, password, passwordConfirm };
  };

  render() {
    const { t } = this.props;

    return (
      <form className="auth-form" onSubmit={this.handleSubmit}>
        <FormGroup controlId="name" validationState={this.validationState('name')}>
          <ControlLabel>{t('Your name')}:</ControlLabel>
          <FormControl
            name="name"
            value={this.state.name}
            onChange={this.handleFieldChange}
            onBlur={this.handleFieldBlur}
          />
          {this.state.errors.name &&
            <HelpBlock>{this.state.errors.name}</HelpBlock>
          }
        </FormGroup>

        <FormGroup controlId="email" validationState={this.validationState('email')}>
          <ControlLabel>{t('Email')}:</ControlLabel>
          <FormControl
            name="email"
            value={this.state.email}
            onChange={this.handleFieldChange}
            onBlur={this.handleFieldBlur}
          />
          {this.state.errors.email &&
            <HelpBlock>{this.state.errors.email}</HelpBlock>
          }
        </FormGroup>

        <FormGroup controlId="password" validationState={this.validationState('password')}>
          <ControlLabel>{t('Password')}:</ControlLabel>
          <FormControl
            name="password"
            type="password"
            value={this.state.password}
            onChange={this.handleFieldChange}
            onBlur={this.handleFieldBlur}
          />
          {this.state.errors.password &&
            <HelpBlock>{this.state.errors.password}</HelpBlock>
          }
        </FormGroup>

        <FormGroup controlId="passwordConfirm" validationState={this.validationState('passwordConfirm')}>
          <ControlLabel>{t('Confirm password')}:</ControlLabel>
          <FormControl
            name="passwordConfirm"
            type="password"
            value={this.state.passwordConfirm}
            onChange={this.handleFieldChange}
            onBlur={this.handleFieldBlur}
          />
          {this.state.errors.passwordConfirm &&
            <HelpBlock>{this.state.errors.passwordConfirm}</HelpBlock>
          }
        </FormGroup>

        <FormGroup className="checkbox-form-group">
          <Checkbox
            className="styled-checkbox"
            name="termsOfAgreement"
            checked={this.state.termsOfAgreement}
            onChange={this.handleCheckboxChange}
          >
            <span className="checkbox-icon" />
            {t('I agree with')} <I18nLink to="/terms_and_conditions/">{t('Terms and Conditions')}</I18nLink>
          </Checkbox>
        </FormGroup>

        <p className="register-info">
          {t('By continuing, you agree with General Delivery Terms, register a free account on our website and let us to contact you via email.')}
        </p>

        <Row>
          <Col lg={6} md={6} xs={6}>
            <Button
              bsStyle="success"
              type="submit"
              className="order-button"
              disabled={!this.state.termsOfAgreement}
            >
              {t('Register')}
            </Button>
          </Col>
          <Col lg={6} md={6} xs={6}>
            <span className="switch-form-link" onClick={this.props.gotoLogin}>{t('Login')}</span>
            <span className="switch-form-link" onClick={this.props.gotoRestorePassword}>{t('Forgot password?')}</span>
          </Col>
        </Row>
      </form>
    );
  }
}

class LoginForm extends Component {
  constructor(...args) {
    super(...args);

    this.state = {
      email: '',
      password: '',
      errors: {},
      errorMessage: ''
    };

    this.validateHandler = defaultValidateHandler.bind(this);
    this.validationState = defaultValidateStatus.bind(this);

    this.handleFieldChange = defaultOnChange.bind(this);
    this.handleFieldBlur = defaultOnBlur.bind(this);
  }

  componentWillMount() {
    const { t } = this.props;

    const rules = {
      email: {
        email: t('Enter valid email address')
      },
      password: {
        required: t('This field may not be blank')
      }
    };

    this.validate = createValidator(rules);
  }

  handleSubmit = (event) => {
    event.preventDefault();

    const { email, password } = this.state;

    const isCredentialsValid = this.validateHandler();
    if (!isCredentialsValid) {
      return;
    }

    this.props.login({ username: email, password }, this.props.nextPage)
      .then(action => {
        if (action.error)
          this.setState({ errorMessage: action.error.detail });
      });
  };

  getProperties = () => {
    const { email, password } = this.state;
    return { email, password };
  };

  render() {
    const { t } = this.props;

    return (
      <form className="auth-form" onSubmit={this.handleSubmit}>
        <FormGroup controlId="email" validationState={this.validationState('email')}>
          <ControlLabel>{t('Email')}:</ControlLabel>
          <FormControl
            name="email"
            value={this.state.email}
            onChange={this.handleFieldChange}
            onBlur={this.handleFieldBlur}
          />
          {this.state.errors.email &&
            <HelpBlock>{this.state.errors.email}</HelpBlock>
          }
        </FormGroup>

        <FormGroup controlId="password" validationState={this.validationState('password')}>
          <ControlLabel>{t('Password')}:</ControlLabel>
          <FormControl
            name="password"
            type="password"
            value={this.state.password}
            onChange={this.handleFieldChange}
            onBlur={this.handleFieldBlur}
          />
          {this.state.errors.password &&
            <HelpBlock>{this.state.errors.password}</HelpBlock>
          }
        </FormGroup>

        {this.state.errorMessage &&
          <HelpBlock className="auth-form-message">{this.state.errorMessage}</HelpBlock>
        }

        <Row>
          <Col lg={6} md={6} xs={6}>
            <Button
              bsStyle="success"
              type="submit"
              className="order-button"
            >
              {t('Login')}
            </Button>
          </Col>
          <Col lg={6} md={6} xs={6}>
            <span className="switch-form-link" onClick={this.props.gotoRegister}>{t('Sign up')}</span>
            <span className="switch-form-link" onClick={this.props.gotoRestorePassword}>{t('Forgot password?')}</span>
          </Col>
        </Row>
      </form>
    );
  }
}

class RestorePasswordForm extends Component {
  constructor(...args) {
    super(...args);

    this.state = {
      mode: 'form',
      email: '',
      errors: {}
    };

    this.validateHandler = defaultValidateHandler.bind(this);
    this.validationState = defaultValidateStatus.bind(this);

    this.handleFieldChange = defaultOnChange.bind(this);
    this.handleFieldBlur = defaultOnBlur.bind(this);
  }

  componentWillMount() {
    const { t } = this.props;

    const rules = {
      email: {
        email: t('Enter valid email address')
      },
    };

    this.validate = createValidator(rules);
  }

  handleSubmit = (event) => {
    event.preventDefault();

    const isFormValid = this.validateHandler();
    if (!isFormValid) return;

    const { email } = this.state;
    const { t } = this.props;
    this.props.restorePassword({ email })
      .then(action => {
        if (action.statusCode === 200) {
          this.setState({ mode: 'success', errors: {} });
        } else {
          this.setState({ errors: { email: t('User with this email was not found on server.') } });
        }
      });
  };

  getProperties = () => ({ email: this.state.email });

  render() {
    const { t } = this.props;

    return (
      <form className="auth-form" onSubmit={this.handleSubmit}>
        {(this.state.mode === 'form') &&
          <FormGroup controlId="email" validationState={this.validationState('email')}>
            <ControlLabel>{t('Email')}:</ControlLabel>

            <FormControl
              name="email"
              value={this.state.email}
              onChange={this.handleFieldChange}
              onBlur={this.handleFieldBlur}
            />

            {this.state.errors.email &&
              <HelpBlock>{this.state.errors.email}</HelpBlock>
            }
          </FormGroup>
        }

        {(this.state.mode === 'success') &&
          <Alert bsStyle="info" className="restore-info">
            {t('Secret link was sent to your email, please check your inbox to restore password.')}
          </Alert>
        }

        <Row>
          <Col lg={6} md={6} xs={6}>
            {(this.state.mode === 'form') &&
              <Button
                bsStyle="success"
                type="submit"
                className="order-button"
              >
                {t('Restore')}
              </Button>
            }
          </Col>

          <Col lg={6} md={6} xs={6}>
            <span className="switch-form-link" onClick={this.props.gotoLogin}>{t('Login')}</span>
            <span className="switch-form-link" onClick={this.props.gotoRegister}>{t('Sign up')}</span>
          </Col>
        </Row>
      </form>
    );
  }
}

class AuthPage extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      mode: 'login'
    };

    this.gotoRegister = this.goto.bind(this, 'register');
    this.gotoLogin = this.goto.bind(this, 'login');
    this.gotoRestorePassword = this.goto.bind(this, 'restorePassword');
  }

  goto(mode) {
    this.setState({ mode });
  }

  render() {
    const { t } = this.props;

    return (
      <div className="auth-page">
        <Grid>
          <Row>
            <Col lg={12} className="title-block">
              <h1 className="title">{t('Create your account on BikesBooking.com')}</h1>
            </Col>
            <Col lg={8} md={7} sm={6} xs={12}>
              <div className="custom-list">
                <ul>
                  <li>
                    <b>{t('Login or register a free customer account, make and manage your bookings online')}</b>
                    <p>{t('If you own a rental company, please use "Add rental company" button above to create a business account')}</p>
                  </li>
                  <li>
                    <b>{t('Book scooters, bicycles, ATV\'s and motorcycles from 900+ providers in 42 countries')}</b>
                  </li>
                  <li>
                    <b>{t('We guarantee the Best prices and also Free cancellations in most of the cases')}</b>
                  </li>
                </ul>
              </div>
            </Col>

            <Col lg={4} md={5} sm={6} xs={12}>
              {(this.state.mode === 'register') &&
                <RegisterForm
                  t={t}
                  registerUser={this.props.registerUser}
                  gotoLogin={this.gotoLogin}
                  gotoRestorePassword={this.gotoRestorePassword}
                />
              }

              {(this.state.mode === 'login') &&
                <LoginForm
                  t={t}
                  login={this.props.login}
                  nextPage={this.props.location.query.next}
                  gotoRegister={this.gotoRegister}
                  gotoRestorePassword={this.gotoRestorePassword}
                />
              }

              {(this.state.mode === 'restorePassword') &&
                <RestorePasswordForm
                  t={t}
                  restorePassword={this.props.restorePassword}
                  gotoRegister={this.gotoRegister}
                  gotoLogin={this.gotoLogin}
                />
              }
            </Col>
          </Row>
        </Grid>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    countries: state.app.countries.items,
  };
}

export default asyncConnect(
  [],
  mapStateToProps,
  { registerUser, login, restorePassword }
)(translate(['translation'])(AuthPage));



// WEBPACK FOOTER //
// ./src/components/AuthPage/index.jsx